/**
 * Ending definitions (R11) — how a pack concludes its story.
 *
 * @remarks
 * An ending is a narrative conclusion the world SELECTS — via the
 * `set_ending` effect, from any interaction, event, or trigger — never a
 * special subsystem. Selecting one appends to `narrative.selectedEndingIds`
 * — a history, in selection order (D151) — and appends the ending's
 * `narratives` as its epilogue lines. Only a `'terminal'` ending closes the
 * slot: selecting anything after it is an engine invariant violation, so two
 * terminal endings on one path stay impossible and are refused loudly.
 *
 * **`kind` is authored per ending** (ruled by Mike, 2026-08-22):
 *
 * - `'terminal'` — the story is over AND the world stops: further player
 *   commands are refused with `refusalNarratives` (or the built-in slot when
 *   none are authored). The classic "you died" / "the end".
 * - `'open'` — the story has concluded but the world remains playable: the
 *   POC's SUBSTATION 3 SECURED is this kind, because Calder's breaker beats
 *   are "the epilogue the player chooses" and must remain open to choose
 *   after it.
 *
 * One kind is not sugar for the other: terminal is a real gate in
 * `processPlayerInput`, and faking "you died, keep walking around" or its
 * inverse in content would be the two-mechanisms smell §2.1 forbids.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { endingIdSchema } from './identifiers.js';
import { narrativeLineSchema } from './transcript.js';

/** The two authored ending postures — see the module remarks. */
export const endingKindSchema = z.enum(['open', 'terminal']);

/** One of the ending kinds. */
export type EndingKind = z.infer<typeof endingKindSchema>;

/** One authored ending — see the module remarks. */
export const endingDefinitionSchema = z
	.strictObject({
		endingId: endingIdSchema,
		kind: endingKindSchema,
		/** The epilogue: appended, in order, the moment this ending is selected. */
		narratives: z.array(narrativeLineSchema).min(1).readonly(),
		/**
		 * Terminal endings only: the authored answer to a player who keeps
		 * typing after the end. Validation refuses this on an `'open'` ending —
		 * an open world never refuses, so the lines could never play (the
		 * D31/D47 dead-content law).
		 */
		refusalNarratives: z.array(narrativeLineSchema).min(1).readonly().optional(),
		/**
		 * D142 (Mike's ruling: death returns to a checkpoint): a TERMINAL
		 * ending that, once its epilogue is spoken, puts the player back at the
		 * world's last checkpoint and lets play continue — Exposure in the
		 * descent arc. The session never ends; the epilogue is the death and
		 * the checkpoint is the waking. Refused on an `'open'` ending (an open
		 * world continues anyway; there is nothing to restart) and, at load, on
		 * a pack with no way to take a checkpoint (dead content).
		 */
		restartAt: z.literal('checkpoint').optional(),
	})
	.readonly()
	.refine((ending) => ending.restartAt === undefined || ending.kind === 'terminal', {
		message: 'restartAt belongs to a terminal ending — an open ending continues anyway, so there is nothing to restart from',
		path: ['restartAt'],
	});

/** One authored ending — see {@link endingDefinitionSchema}. */
export type EndingDefinition = z.infer<typeof endingDefinitionSchema>;
